<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref } from 'vue'
import ResourcePickerModal from '@/features/virtual-resource/components/ResourcePickerModal.vue'
import type { AdvancedTextImportRow, BasicTextImportRow } from '@/features/text-import/textImportLogic'
import {
  TEXT_IMPORT_ADVANCED_GRID_COLUMNS,
  TEXT_IMPORT_BASIC_GRID_COLUMNS,
  canAdvancedTextImportRowCopy,
  canAdvancedTextImportRowDelete,
  canAdvancedTextImportRowPasteAt,
  getAdvancedTextImportRowTypeLabel,
} from '@/features/text-import/textImportResultList'
import {
  TEXT_IMPORT_ROW_MENU_CLASS,
  bindTextImportRowMenuDismiss,
  textImportRowContextKey,
  unbindTextImportRowMenuDismiss,
} from '@/features/text-import/textImportRowContext'
import {
  resolveFullDisplayPathFromFileName,
  resolveStoredPathFromDisplayPath,
} from '@/features/virtual-resource/fileNamePath'
import { useWorkspaceStore } from '@/store/workspace'

type BasicEditField = 'mapName' | 'name' | 'content'
type AdvancedEditField = 'mapName' | 'personName' | 'content' | 'optionContent'
type RowEditField = BasicEditField | AdvancedEditField | 'audio' | 'graphic'

const EDIT_DOUBLE_TAP_MS = 650
const editTapState = ref<{ field: RowEditField; at: number } | null>(null)

const props = defineProps<{
  variant: 'basic' | 'advanced'
  selected: boolean
  basicRow?: BasicTextImportRow
  advancedRow?: AdvancedTextImportRow
}>()

const emit = defineEmits<{
  select: []
  advancedContentBlur: []
}>()

const rowContext = inject(textImportRowContextKey, null)
const workspace = useWorkspaceStore()
const editingField = ref<BasicEditField | AdvancedEditField | null>(null)
const editInputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
const audioPickerOpen = ref(false)
const graphicPickerOpen = ref(false)
const menuOpen = ref(false)
const menuX = ref(0)
const menuY = ref(0)

const isAdvancedOptionRow = computed(() => Boolean(props.advancedRow?.optionKey.type))

const canRowCopy = computed(() => {
  if (props.variant !== 'advanced' || !props.advancedRow) return true
  return canAdvancedTextImportRowCopy(props.advancedRow)
})

const canRowDelete = computed(() => {
  if (props.variant !== 'advanced' || !props.advancedRow) return true
  return canAdvancedTextImportRowDelete(props.advancedRow)
})

const canRowPaste = computed(() => {
  if (props.variant !== 'advanced' || !props.advancedRow) return true
  return canAdvancedTextImportRowPasteAt(props.advancedRow)
})

const canOpenRowMenu = computed(() => canRowCopy.value || canRowDelete.value || canRowPaste.value)

const advancedTypeLabel = computed(() => {
  if (!props.advancedRow) return ''
  return getAdvancedTextImportRowTypeLabel(props.advancedRow)
})

function formatAudioPath(path: string): string {
  const root = workspace.virtualResourceRoots.audio
  return resolveFullDisplayPathFromFileName(root, 'audio', path, 'mapped', '音乐')
}

function formatGraphicPath(path: string): string {
  const root = workspace.virtualResourceRoots.graphic
  return resolveFullDisplayPathFromFileName(root, 'graphic', path, 'mapped', '背景')
}

function handleRowClick(): void {
  emit('select')
}

function canStartRowEdit(field: RowEditField): boolean {
  if (props.variant === 'advanced' && props.advancedRow?.optionKey.type) {
    return field === 'optionContent' && props.advancedRow.optionKey.type === 'item'
  }
  if (field === 'audio' || field === 'graphic') {
    return !isAdvancedOptionRow.value
  }
  return true
}

function triggerRowEdit(field: RowEditField, event: Event): void {
  if (!canStartRowEdit(field)) return
  if (field === 'audio') {
    openAudioPicker(event as MouseEvent)
    return
  }
  if (field === 'graphic') {
    openGraphicPicker(event as MouseEvent)
    return
  }
  startTextEdit(field, event as MouseEvent)
}

/**
 * 单元格点击进入编辑：PC 用 detail>=2；平板两次 click 间隔内二次点击。
 */
function handleEditCellClick(field: RowEditField, event: MouseEvent): void {
  event.stopPropagation()
  if (!canStartRowEdit(field)) {
    emit('select')
    return
  }
  if (event.detail >= 2) {
    editTapState.value = null
    triggerRowEdit(field, event)
    return
  }
  const now = Date.now()
  const prev = editTapState.value
  if (prev?.field === field && now - prev.at <= EDIT_DOUBLE_TAP_MS) {
    editTapState.value = null
    triggerRowEdit(field, event)
    return
  }
  editTapState.value = { field, at: now }
  emit('select')
}

function startTextEdit(field: BasicEditField | AdvancedEditField, event: MouseEvent): void {
  event.stopPropagation()
  if (props.variant === 'advanced' && props.advancedRow?.optionKey.type) {
    if (field !== 'optionContent') return
  }
  editingField.value = field
  void nextTick(() => {
    editInputRef.value?.focus()
    if (editInputRef.value instanceof HTMLTextAreaElement) {
      editInputRef.value.select()
    } else if (editInputRef.value instanceof HTMLInputElement) {
      editInputRef.value.select()
    }
  })
}

function endTextEdit(): void {
  if (editingField.value === 'content' && props.variant === 'advanced') {
    emit('advancedContentBlur')
  }
  editingField.value = null
}

function openAudioPicker(event: MouseEvent): void {
  event.stopPropagation()
  if (isAdvancedOptionRow.value) return
  emit('select')
  audioPickerOpen.value = true
}

function openGraphicPicker(event: MouseEvent): void {
  event.stopPropagation()
  if (isAdvancedOptionRow.value) return
  emit('select')
  graphicPickerOpen.value = true
}

function toEngineStoredPath(category: 'audio' | 'graphic', value: string, baseFolderPath: string): string {
  const root = workspace.virtualResourceRoots[category]
  return resolveStoredPathFromDisplayPath(root, category, value, 'engine', baseFolderPath)
}

function setBasicAudio(path: string): void {
  if (!props.basicRow) return
  props.basicRow.backMusic = toEngineStoredPath('audio', path, '音乐')
}

function setBasicGraphic(path: string): void {
  if (!props.basicRow) return
  props.basicRow.backPic = toEngineStoredPath('graphic', path, '背景')
}

function setAdvancedAudio(path: string): void {
  if (!props.advancedRow) return
  props.advancedRow.bgm = toEngineStoredPath('audio', path, '音乐')
}

function setAdvancedGraphic(path: string): void {
  if (!props.advancedRow) return
  props.advancedRow.backPic = toEngineStoredPath('graphic', path, '背景')
}

function closeMenu(): void {
  menuOpen.value = false
  unbindTextImportRowMenuDismiss()
}

function openMenu(clientX: number, clientY: number): void {
  const width = 88
  const height = 96
  const margin = 8
  menuX.value = Math.max(margin, Math.min(clientX, window.innerWidth - width - margin))
  menuY.value = Math.max(margin, Math.min(clientY, window.innerHeight - height - margin))
  menuOpen.value = true
  bindTextImportRowMenuDismiss(closeMenu)
}

function handleContextMenu(event: MouseEvent): void {
  event.preventDefault()
  emit('select')
  if (!canOpenRowMenu.value) return
  openMenu(event.clientX, event.clientY)
}

function handleMoreClick(event: MouseEvent): void {
  event.preventDefault()
  event.stopPropagation()
  emit('select')
  if (!canOpenRowMenu.value) return
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openMenu(rect.left, rect.bottom)
}

function handleMenuAction(action: 'copy' | 'paste' | 'delete'): void {
  const row = props.basicRow ?? props.advancedRow
  if (!row) return
  if (action === 'copy' && !canRowCopy.value) return
  if (action === 'paste' && !canRowPaste.value) return
  if (action === 'delete' && !canRowDelete.value) return
  rowContext?.runRowAction(props.variant, row, action)
  closeMenu()
}

onBeforeUnmount(() => {
  if (menuOpen.value) closeMenu()
})
</script>

<template>
  <div
    class="text-import-result-row"
    :class="{
      'text-import-result-row--selected': selected,
      'text-import-result-row--basic': variant === 'basic',
      'text-import-result-row--advanced': variant === 'advanced',
      'text-import-result-row--option-start': variant === 'advanced' && advancedRow?.optionKey.type === 'start',
      'text-import-result-row--option-item': variant === 'advanced' && advancedRow?.optionKey.type === 'item',
      'text-import-result-row--option-end': variant === 'advanced' && advancedRow?.optionKey.type === 'end',
    }"
    @click="handleRowClick"
    @contextmenu.prevent="handleContextMenu"
  >
    <template v-if="variant === 'basic' && basicRow">
      <div class="row-cell-wrap">
        <input
          v-if="editingField === 'mapName'"
          ref="editInputRef"
          v-model="basicRow.mapName"
          class="row-cell-input"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell"
          data-edit-field="mapName"
          :class="{ 'row-cell--muted': !basicRow.mapName }"
          @click.stop="handleEditCellClick('mapName', $event)"
        >
          {{ basicRow.mapName }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <span
          class="row-cell row-cell--resource"
          data-edit-field="audio"
          :class="{ 'row-cell--muted': !basicRow.backMusic }"
          @click.stop="handleEditCellClick('audio', $event)"
        >
          {{ formatAudioPath(basicRow.backMusic) || '请选择资源' }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <span
          class="row-cell row-cell--resource"
          data-edit-field="graphic"
          :class="{ 'row-cell--muted': !basicRow.backPic }"
          @click.stop="handleEditCellClick('graphic', $event)"
        >
          {{ formatGraphicPath(basicRow.backPic) || '请选择资源' }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <input
          v-if="editingField === 'name'"
          ref="editInputRef"
          v-model="basicRow.name"
          class="row-cell-input"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell"
          data-edit-field="name"
          :class="{ 'row-cell--muted': !basicRow.name }"
          @click.stop="handleEditCellClick('name', $event)"
        >
          {{ basicRow.name }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <textarea
          v-if="editingField === 'content'"
          ref="editInputRef"
          v-model="basicRow.content"
          class="row-cell-input row-cell-input--textarea"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell row-cell--multiline"
          data-edit-field="content"
          :class="{ 'row-cell--muted': !basicRow.content }"
          @click.stop="handleEditCellClick('content', $event)"
        >
          {{ basicRow.content }}
        </span>
      </div>
      <ResourcePickerModal
        v-if="audioPickerOpen"
        v-model:show="audioPickerOpen"
        category="audio"
        title="选择背景音乐"
        :selected-path="basicRow.backMusic"
        base-folder-path="音乐"
        @select="setBasicAudio"
      />
      <ResourcePickerModal
        v-if="graphicPickerOpen"
        v-model:show="graphicPickerOpen"
        category="graphic"
        title="选择背景图"
        :selected-path="basicRow.backPic"
        base-folder-path="背景"
        @select="setBasicGraphic"
      />
    </template>

    <template v-else-if="variant === 'advanced' && advancedRow">
      <div class="row-cell-wrap">
        <span class="row-cell row-cell--type">{{ advancedTypeLabel }}</span>
      </div>
      <div class="row-cell-wrap">
        <input
          v-if="editingField === 'mapName' && !advancedRow.optionKey.type"
          ref="editInputRef"
          v-model="advancedRow.mapName"
          class="row-cell-input"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell"
          data-edit-field="mapName"
          :class="{ 'row-cell--muted': !advancedRow.mapName && !advancedRow.optionKey.type }"
          @click.stop="handleEditCellClick('mapName', $event)"
        >
          {{ advancedRow.mapName }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <span
          class="row-cell row-cell--resource"
          data-edit-field="audio"
          :class="{ 'row-cell--muted': !advancedRow.bgm }"
          @click.stop="handleEditCellClick('audio', $event)"
        >
          {{ formatAudioPath(advancedRow.bgm) || '请选择资源' }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <span
          class="row-cell row-cell--resource"
          data-edit-field="graphic"
          :class="{ 'row-cell--muted': !advancedRow.backPic }"
          @click.stop="handleEditCellClick('graphic', $event)"
        >
          {{ formatGraphicPath(advancedRow.backPic) || '请选择资源' }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <input
          v-if="editingField === 'personName' && !advancedRow.optionKey.type"
          ref="editInputRef"
          v-model="advancedRow.person.name"
          class="row-cell-input"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell"
          data-edit-field="personName"
          :class="{ 'row-cell--muted': !advancedRow.person.name && !advancedRow.optionKey.type }"
          @click.stop="handleEditCellClick('personName', $event)"
        >
          {{ advancedRow.person.name }}
        </span>
      </div>
      <div class="row-cell-wrap">
        <span
          v-if="advancedRow.optionKey.type === 'start'"
          class="row-cell row-cell--option-marker"
        >
          {{ advancedRow.optionKey.text || '选项开始' }}
        </span>
        <span
          v-else-if="advancedRow.optionKey.type === 'end'"
          class="row-cell row-cell--option-marker"
        >
          {{ advancedRow.optionKey.text || '选项结束' }}
        </span>
        <textarea
          v-else-if="editingField === 'optionContent' && advancedRow.optionKey.type === 'item'"
          ref="editInputRef"
          v-model="advancedRow.optionContent"
          class="row-cell-input row-cell-input--option"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else-if="advancedRow.optionKey.type === 'item'"
          class="row-cell row-cell--multiline row-cell--option-item-text"
          data-edit-field="optionContent"
          :class="{ 'row-cell--muted': !advancedRow.optionContent }"
          @click.stop="handleEditCellClick('optionContent', $event)"
        >
          {{ advancedRow.optionContent || '（空选项）' }}
        </span>
        <textarea
          v-else-if="editingField === 'content'"
          ref="editInputRef"
          v-model="advancedRow.textContent"
          class="row-cell-input row-cell-input--textarea"
          @click.stop
          @blur="endTextEdit"
        />
        <span
          v-else
          class="row-cell row-cell--multiline"
          data-edit-field="content"
          :class="{ 'row-cell--muted': !advancedRow.textContent }"
          @click.stop="handleEditCellClick('content', $event)"
        >
          {{ advancedRow.textContent }}
        </span>
      </div>
      <ResourcePickerModal
        v-if="audioPickerOpen"
        v-model:show="audioPickerOpen"
        category="audio"
        title="选择背景音乐"
        :selected-path="advancedRow.bgm"
        base-folder-path="音乐"
        @select="setAdvancedAudio"
      />
      <ResourcePickerModal
        v-if="graphicPickerOpen"
        v-model:show="graphicPickerOpen"
        category="graphic"
        title="选择背景图"
        :selected-path="advancedRow.backPic"
        base-folder-path="背景"
        @select="setAdvancedGraphic"
      />
    </template>

    <span
      v-if="canOpenRowMenu"
      class="row-more"
      role="button"
      tabindex="0"
      title="更多操作"
      aria-label="更多操作"
      @click="handleMoreClick"
      @pointerdown.stop
    >
      更多
    </span>

    <Teleport to="body">
      <div
        v-if="menuOpen"
        :class="TEXT_IMPORT_ROW_MENU_CLASS"
        :style="{ left: `${menuX}px`, top: `${menuY}px` }"
      >
        <button
          type="button"
          :disabled="!canRowCopy"
          @mousedown.stop.prevent="handleMenuAction('copy')"
        >
          复制
        </button>
        <button
          type="button"
          :disabled="!canRowPaste"
          @mousedown.stop.prevent="handleMenuAction('paste')"
        >
          粘贴
        </button>
        <button
          type="button"
          :disabled="!canRowDelete"
          @mousedown.stop.prevent="handleMenuAction('delete')"
        >
          删除
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.text-import-result-row {
  position: absolute;
  left: 0;
  width: 100%;
  display: grid;
  gap: 1px;
  align-items: start;
  padding: 4px 0;
  box-sizing: border-box;
  overflow: hidden;
  border-bottom: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  cursor: default;
}

.text-import-result-row--basic {
  grid-template-columns: v-bind(TEXT_IMPORT_BASIC_GRID_COLUMNS);
}

.text-import-result-row--advanced {
  grid-template-columns: v-bind(TEXT_IMPORT_ADVANCED_GRID_COLUMNS);
}

.text-import-result-row--selected {
  background: var(--theme-bg-selected);
}

.row-cell-wrap {
  min-width: 0;
}

.row-cell {
  display: block;
  min-height: 28px;
  padding: 4px 6px;
  color: var(--theme-text-heading);
  font-size: 12px;
  line-height: 20px;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-cell--multiline {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 28px;
  text-overflow: clip;
}

.row-cell--type {
  color: var(--theme-text-heading);
  font-weight: 600;
  white-space: nowrap;
}

.row-cell--muted {
  color: var(--theme-text-secondary);
}

.text-import-result-row--option-start .row-cell--type,
.text-import-result-row--option-item .row-cell--type,
.text-import-result-row--option-end .row-cell--type,
.row-cell--option-marker,
.row-cell--option-item-text {
  color: #c97a16;
  font-weight: 600;
}

.row-cell-input {
  width: 100%;
  min-width: 0;
  min-height: 28px;
  padding: 4px 6px;
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  font: inherit;
  font-size: 12px;
  line-height: 20px;
  box-sizing: border-box;
}

.text-import-result-row--selected .row-cell-input {
  background: var(--theme-bg-selected);
}

.row-cell-input--textarea {
  resize: vertical;
  min-height: 28px;
  line-height: 28px;
  white-space: pre-wrap;
}

.row-cell-input--option {
  color: #f0a020;
}

.row-more {
  align-self: start;
  margin-top: 6px;
  padding: 2px 4px;
  color: var(--theme-text-very-muted);
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
}

.row-more:hover,
.row-more:focus-visible {
  color: var(--theme-text-secondary);
}

.text-import-result-row--selected .row-more {
  color: var(--theme-text-heading);
}
</style>

<style>
.text-import-row-context-menu {
  position: fixed;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  min-width: 88px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.text-import-row-context-menu button {
  padding: 6px 10px;
  border: 0;
  background: transparent;
  color: var(--theme-text-primary);
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}

.text-import-row-context-menu button:hover {
  background: var(--theme-bg-selected);
}

.text-import-row-context-menu button:disabled {
  color: var(--theme-text-very-muted);
  cursor: not-allowed;
}

.text-import-row-context-menu button:disabled:hover {
  background: transparent;
}
</style>
